import { useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useAbaPendente } from "../lib/open-file.js";
import { InvoiceDetailModal } from "../components/domain/InvoiceDetailModal.js";
import { useT } from "../i18n/index.js";
import { sugerirTipoCliente } from "@eurostorm/shared";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { CustomerReviewModal } from "../components/domain/CustomerReviewModal.js";
import { CustomerCreditCard } from "../components/domain/CustomerCredit.js";
import { CustomerStatementModal } from "../components/domain/CustomerStatementModal.js";
import { VehicleHistoryModal } from "../components/domain/VehicleHistoryModal.js";
import { CarLogo } from "../components/common/CarLogo.js";
import { Paginacao } from "../components/common/Paginacao.js";
import { VEHICLE_STATUS_STYLE } from "../lib/vehicle-status.js";
import { CustomerModal } from "./Customers.js";
import { useModals } from "../components/common/ModalsProvider.js";

/**
 * F7.3 — Detalhe de cliente.
 *
 * 6 abas: Resumo · Dossiers · Faturas · Recebimentos · Veículos · Audit.
 * Janela temporal global (default 12M) afeta KPIs e listas.
 */

type TabKey = "resumo" | "dossiers" | "faturas" | "recebimentos" | "veiculos" | "worklog" | "audit";

/**
 * CEO 2026-11-03 ("não consigo ver quais veículos ainda estão em Devis neste
 * cliente") — etapa atual do dossier, mesma regra/cores da lista de veículos.
 */
type Etapa = "devis" | "em_reparacao" | "reparado" | "faturado" | "recebido" | "cadastro";
const ETAPAS: Etapa[] = ["devis", "em_reparacao", "reparado", "faturado", "recebido", "cadastro"];
const ETAPA_STYLE: Record<Etapa, { bg: string; label: string }> = {
  devis: VEHICLE_STATUS_STYLE.devis!,
  em_reparacao: VEHICLE_STATUS_STYLE.em_reparacao!,
  reparado: VEHICLE_STATUS_STYLE.reparado!,
  faturado: VEHICLE_STATUS_STYLE.faturado!,
  recebido: { bg: VEHICLE_STATUS_STYLE.recebido_pago!.bg, label: "customerDetail.stageReceived" },
  cadastro: VEHICLE_STATUS_STYLE.cadastro!,
};

const TAB_KEYS: TabKey[] = [
  "resumo",
  "dossiers",
  "faturas",
  "recebimentos",
  "veiculos",
  "worklog",
  "audit",
];

type Period = "since" | "30d" | "3m" | "6m" | "12m" | "year" | "all";
/** CEO 2026-10-11: início do Gestion — janela padrão. */
const GESTION_START = "2026-05-01";

function periodRange(p: Period): { periodStart?: string; periodEnd?: string } {
  if (p === "all") return {};
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  if (p === "since") return { periodStart: GESTION_START, periodEnd: end };
  if (p === "year") {
    return {
      periodStart: `${now.getFullYear()}-01-01`,
      periodEnd: end,
    };
  }
  const start = new Date(now);
  if (p === "30d") start.setDate(start.getDate() - 30);
  else if (p === "3m") start.setMonth(start.getMonth() - 3);
  else if (p === "6m") start.setMonth(start.getMonth() - 6);
  else start.setMonth(start.getMonth() - 12); // 12m default
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end,
  };
}

const INVOICE_STATUS_TONE: Record<string, string> = {
  issued: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
  void: "bg-neutral-700/40 text-neutral-500",
};

const DOSSIER_STATUS_TONE: Record<string, string> = {
  pending: "bg-neutral-700/40 text-neutral-300",
  issued: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};
export default function CustomerDetail() {
  const params = useParams<{ id: string }>();
  const customerId = Number(params.id);
  const { user } = useAuth();
  const [tab, setTab] = useState<TabKey>("resumo");
  // Etapa escolhida na aba Dossiers (também pelos contadores do Resumo).
  const [etapaDossiers, setEtapaDossiers] = useState<Etapa | null>(null);
  const [period, setPeriod] = useState<Period>("since");
  const tr = useT();

  const PERIOD_LABELS: Record<Period, string> = {
    since: tr("customerDetail.periodSince"),
    "30d": tr("customerDetail.period30d"),
    "3m": tr("customerDetail.period3m"),
    "6m": tr("customerDetail.period6m"),
    "12m": tr("customerDetail.period12m"),
    year: tr("customerDetail.periodYear"),
    all: tr("customerDetail.periodAll"),
  };
  const TAB_LABELS: Record<TabKey, string> = {
    resumo: tr("customerDetail.tabResumo"),
    dossiers: tr("customerDetail.tabDossiers"),
    faturas: tr("customerDetail.tabFaturas"),
    recebimentos: tr("customerDetail.tabRecebimentos"),
    veiculos: tr("customerDetail.tabVeiculos"),
    worklog: tr("customerDetail.tabWorklog"),
    audit: tr("customerDetail.tabAudit"),
  };

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  // Probe paralelo para descobrir a empresa do customer (similar a DossierDetail).
  const candidateCompanies = useMemo(
    () => accessibleCompanies.map((c) => c.id),
    [accessibleCompanies],
  );
  const probeQueries = trpc.useQueries((t) =>
    candidateCompanies.map((cid) =>
      t.customers.getById(
        { companyId: cid, customerId },
        { retry: false, refetchOnMount: false, enabled: !Number.isNaN(customerId) },
      ),
    ),
  );
  const probeIndex = probeQueries.findIndex((q) => q.data != null);
  const companyId: number | null =
    probeIndex >= 0 ? (candidateCompanies[probeIndex] ?? null) : null;
  const customer = probeIndex >= 0 ? (probeQueries[probeIndex]?.data ?? null) : null;

  const range = useMemo(() => periodRange(period), [period]);

  if (Number.isNaN(customerId)) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{tr("customerDetail.invalidId")}</p>
      </Shell>
    );
  }
  if (!user) return null;

  const allLoading = probeQueries.length > 0 && probeQueries.every((q) => q.isLoading);
  if (allLoading) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{tr("customerDetail.loading")}</p>
      </Shell>
    );
  }
  if (!customer || companyId === null) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{tr("customerDetail.customerNotFound")}</p>
        <Link href="/clientes" className="text-sm text-lime hover:text-lime">
          ← {tr("customerDetail.backToList")}
        </Link>
      </Shell>
    );
  }

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  return (
    <Shell>
      <Header customer={customer} companyId={companyId} />
      {/* CEO 2026-10-27 — sobra de depósito vira crédito do cliente. */}
      <CustomerCreditCard companyId={companyId} customerId={customer.id} />

      {/* Period selector */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-neutral-500">
          {tr("customerDetail.periodSelectorLabel")}
        </label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className={inputClass + " w-48"}
        >
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <option key={p} value={p}>
              {PERIOD_LABELS[p]}
            </option>
          ))}
        </select>
        {range.periodStart && (
          <span className="text-xs text-neutral-500">
            {range.periodStart} → {range.periodEnd}
          </span>
        )}
      </div>

      <nav className="rounded-lg border border-neutral-800 bg-neutral-900 px-2 py-1 flex flex-wrap gap-1">
        {/* F-worklog-atelier — a aba Worklog é só do admin. */}
        {TAB_KEYS.filter((k) => k !== "worklog" || isAdmin).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              tab === k
                ? "bg-lime/20 text-lime border border-lime/40"
                : "hover:bg-neutral-800 text-neutral-400 border border-transparent"
            }`}
          >
            {TAB_LABELS[k]}
          </button>
        ))}
      </nav>

      {tab === "resumo" && (
        <TabResumo
          companyId={companyId}
          customerId={customer.id}
          customer={customer}
          range={range}
          onEtapa={(e) => {
            setEtapaDossiers(e);
            setTab("dossiers");
          }}
        />
      )}
      {tab === "dossiers" && (
        <TabDossiers
          companyId={companyId}
          customerId={customer.id}
          range={range}
          etapa={etapaDossiers}
          onEtapa={setEtapaDossiers}
        />
      )}
      {tab === "faturas" && (
        <TabFaturas companyId={companyId} customerId={customer.id} range={range} />
      )}
      {tab === "recebimentos" && (
        <TabRecebimentos companyId={companyId} customerId={customer.id} range={range} />
      )}
      {tab === "veiculos" && <TabVeiculos companyId={companyId} customerId={customer.id} />}
      {tab === "worklog" && isAdmin && (
        <TabWorklog
          companyId={companyId}
          customerId={customer.id}
          customerEmail={customer.email ?? customer.billingEmail ?? null}
        />
      )}
      {tab === "audit" && (
        <TabAudit companyId={companyId} customerId={customer.id} isAdmin={isAdmin} />
      )}
    </Shell>
  );
}

// ============================================================================
// HEADER
// ============================================================================

function Header({
  customer,
  companyId,
}: {
  customer: {
    id: number;
    displayName: string;
    legalName: string | null;
    kind: string;
    fiscalId?: string | null;
    vatNumber?: string | null;
    defaultRetentionPct?: string | number | null;
    chantierId?: number | null;
    reviewPending?: boolean;
  };
  companyId: number;
}) {
  const t = useT();
  const canEdit = useCanEdit();
  const utils = trpc.useUtils();
  const [revisando, setRevisando] = useState(false);
  const [editando, setEditando] = useState(false);
  // CEO 2026-10-27 (opção C) — o recado segue a sugestão: nome de pessoa →
  // "parece um particular?"; nome de empresa → "falta a remise".
  const sugestao = sugerirTipoCliente({
    nome: customer.displayName,
    siret: customer.fiscalId,
    tva: customer.vatNumber,
    razaoSocial: customer.legalName,
  });
  // CEO 2026-10-27 (MELANIE FAURE) — PRO sem SIRET, TVA nem remise: parece
  // particular; sem remise a comissão em base orçamento não é calculada.
  const pareceParticular =
    customer.kind === "business" &&
    !customer.fiscalId &&
    !customer.vatNumber &&
    customer.defaultRetentionPct == null;
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 flex items-start justify-between">
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">{customer.displayName}</h2>
          <span className="rounded px-2 py-0.5 text-xs bg-neutral-800 text-neutral-400">
            {customer.kind === "individual"
              ? t("customers.kindIndividual")
              : customer.kind === "business"
                ? t("customers.kindBusiness")
                : customer.kind === "insurer"
                  ? t("customers.kindInsurer")
                  : customer.kind}
          </span>
        </div>
        {customer.legalName && (
          <p className="text-xs text-neutral-500 mt-1">{customer.legalName}</p>
        )}
        {customer.reviewPending && (
          <div
            className="mt-2 flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 text-xs leading-snug"
            style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
            data-testid="customer-detail-review"
          >
            <span>
              🔎 {t("customerReview.bannerPre")} {t("customerReview.suggestionPre")}{" "}
              <strong>
                {sugestao.kind === "individual"
                  ? t("customerReview.kindIndividual")
                  : t("customerReview.kindBusiness")}
              </strong>
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={() => setRevisando(true)}
                className="rounded border px-2 py-0.5 font-semibold"
                style={{ borderColor: "#D97706" }}
                data-testid="customer-detail-review-btn"
              >
                {t("customerReview.reviewBtn")}
              </button>
            )}
          </div>
        )}
        {pareceParticular && !customer.reviewPending && (
          <p
            className="mt-2 rounded-md border px-2 py-1 text-xs leading-snug"
            style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
            data-testid="customer-detail-pro-looks-individual"
          >
            ⚠{" "}
            {sugestao.kind === "individual"
              ? t("customers.proLooksIndividual")
              : t("customerReview.proMissingRemise")}
          </p>
        )}
        {revisando && (
          <CustomerReviewModal
            companyId={companyId}
            customer={customer}
            onClose={() => setRevisando(false)}
            showOpenRecord={false}
          />
        )}
      </div>
      {/* CEO 2026-11-01 — editar o cadastro direto da ficha (antes só pela lista). */}
      <div className="flex shrink-0 flex-col items-end gap-2">
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="rounded-md bg-lime px-3 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright"
            data-testid="customer-detail-edit"
          >
            ✎ {t("customerDetail.editBtn")}
          </button>
        )}
        <Link href="/clientes" className="text-sm text-neutral-400 hover:text-lime">
          ← {t("customerDetail.backLink")}
        </Link>
      </div>
      {editando && (
        <CustomerModal
          companyId={companyId}
          mode="edit"
          customerId={customer.id}
          onClose={() => setEditando(false)}
          onSaved={() => {
            setEditando(false);
            void utils.customers.invalidate();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// TAB: RESUMO
// ============================================================================

function TabResumo({
  companyId,
  customerId,
  customer,
  range,
  onEtapa,
}: {
  companyId: number;
  customerId: number;
  customer: {
    fiscalId: string | null;
    countryCode: string;
    email: string | null;
    phone: string | null;
    preferredLang: string;
    paymentTermDays: number;
    active: boolean;
  };
  range: { periodStart?: string; periodEnd?: string };
  /** CEO 2026-11-03 — clicar num contador abre a aba Dossiers nessa etapa. */
  onEtapa: (e: Etapa) => void;
}) {
  const t = useT();
  const etapasQ = trpc.customers.stageCounts.useQuery({ companyId, customerId });
  const opQuery = trpc.customers.getOperationalSummary.useQuery({
    companyId,
    customerId,
    ...range,
  });
  // F-releve-conta (CEO 2026-11-03)
  const [releveAberto, setReleveAberto] = useState(false);
  const finQuery = trpc.customers.getFinancialSummary.useQuery({
    companyId,
    customerId,
    ...range,
  });

  const op = opQuery.data;
  const fin = finQuery.data;
  const ticket = op && op.dossierCount > 0 ? op.totalFaturado / op.dossierCount : null;

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Cadastro */}
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("customerDetail.sectionRecord")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          <Row label={t("customerDetail.fiscalId")} value={customer.fiscalId ?? "—"} mono />
          <Row label={t("customerDetail.country")} value={customer.countryCode} />
          <Row label={t("customerDetail.email")} value={customer.email ?? "—"} />
          <Row label={t("customerDetail.phone")} value={customer.phone ?? "—"} />
          <Row label={t("customerDetail.language")} value={customer.preferredLang} />
          <Row
            label={t("customerDetail.paymentTerm")}
            value={`${customer.paymentTermDays} ${t("customerDetail.daysUnit")}`}
          />
          <Row
            label={t("customerDetail.statusLabel")}
            value={
              customer.active ? t("customerDetail.activeValue") : t("customerDetail.inactiveValue")
            }
            badge={customer.active ? "bg-lime/15 text-lime" : "bg-neutral-700/40 text-neutral-400"}
          />
        </dl>
      </section>

      {/* KPIs operacionais */}
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("customerDetail.sectionOperational")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          <Row label={t("customerDetail.tabDossiers")} value={op ? String(op.dossierCount) : "…"} />
          <Row
            label={t("customerDetail.registeredVehicles")}
            value={op ? String(op.vehicleCount) : "…"}
          />
          <Row
            label={t("customerDetail.invoicedHt")}
            value={op ? formatMoney(op.totalFaturado, { decimals: 2 }) : "…"}
            mono
          />
          <Row
            label={t("customerDetail.avgTicket")}
            value={ticket != null ? formatMoney(ticket, { decimals: 2 }) : "—"}
            mono
          />
          <Row
            label={t("customerDetail.totalRetention")}
            value={op ? formatMoney(op.totalRetencao, { decimals: 2 }) : "…"}
            mono
          />
        </dl>
        <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500">
          {t("customerDetail.stageToday")}
        </p>
        <div className="mt-1 flex flex-wrap gap-2" data-testid="customer-stage-counters">
          {(["devis", "em_reparacao", "reparado"] as const).map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onEtapa(e)}
              className="inline-flex items-center gap-2 rounded-md border border-neutral-700 px-2.5 py-1 text-xs hover:border-lime"
              data-testid={`customer-stage-${e}`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: ETAPA_STYLE[e].bg }}
              />
              {t(ETAPA_STYLE[e].label as Parameters<typeof t>[0])}
              <strong className="font-mono">{etapasQ.data ? etapasQ.data[e] : "…"}</strong>
            </button>
          ))}
        </div>
      </section>

      {/* KPIs financeiros */}
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 col-span-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
            {t("customerDetail.sectionFinancial")}
          </h3>
          <button
            type="button"
            onClick={() => setReleveAberto(true)}
            className="btn text-xs"
            data-testid="customer-statement-btn"
          >
            {t("customerDetail.statementBtn")}
          </button>
        </div>
        {releveAberto && (
          <CustomerStatementModal
            companyId={companyId}
            customerId={customerId}
            onClose={() => setReleveAberto(false)}
          />
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <KpiBlock
            label={t("customerDetail.invoicesIssuedPaid")}
            value={fin ? String(fin.invoiceCount) : "…"}
          />
          <KpiBlock
            label={t("customerDetail.totalIssued")}
            value={fin ? formatMoney(fin.totalIssued, { decimals: 2 }) : "…"}
          />
          <KpiBlock
            label={t("customerDetail.totalReceived")}
            value={fin ? formatMoney(fin.totalPaid, { decimals: 2 }) : "…"}
          />
          <KpiBlock
            label={t("customerDetail.totalCredited")}
            value={fin ? formatMoney(fin.totalCredited, { decimals: 2 }) : "…"}
          />
          <KpiBlock
            label={t("customerDetail.openBalance")}
            value={fin ? formatMoney(fin.openBalance, { decimals: 2 }) : "…"}
            sub={fin ? `${formatMoney(fin.openBalanceTtc, { decimals: 2 })} TTC` : undefined}
            tone={
              fin && fin.openBalance > 0
                ? "amber"
                : fin && fin.openBalance === 0
                  ? "positive"
                  : undefined
            }
          />
          <KpiBlock
            label={t("customerDetail.avgDaysToPayment")}
            value={
              fin?.avgDaysToPayment != null
                ? `${fin.avgDaysToPayment.toFixed(1)} ${t("customerDetail.dayAbbr")}`
                : "—"
            }
          />
          <KpiBlock
            label={t("customerDetail.overdueInvoices")}
            value={fin ? String(fin.overdueCount) : "…"}
            tone={fin && fin.overdueCount > 0 ? "red" : undefined}
          />
          <KpiBlock
            label={t("customerDetail.overdueBalance")}
            value={fin ? formatMoney(fin.overdueAmount, { decimals: 2 }) : "…"}
            sub={fin ? `${formatMoney(fin.overdueAmountTtc, { decimals: 2 })} TTC` : undefined}
            tone={fin && fin.overdueAmount > 0 ? "red" : undefined}
          />
        </div>
      </section>

      {/* CEO 2026-11-01 — pessoas de contato do cliente. */}
      <ContatosCliente companyId={companyId} customerId={customerId} />
    </div>
  );
}

/**
 * Contatos do cliente (compras, contabilidade, chefe de oficina…). "Recebe
 * faturas" = sugerido primeiro no envio da fatura por e-mail.
 */
function ContatosCliente({ companyId, customerId }: { companyId: number; customerId: number }) {
  const t = useT();
  const canEdit = useCanEdit();
  const modals = useModals();
  const utils = trpc.useUtils();
  const q = trpc.customers.listContacts.useQuery({ companyId, customerId });
  const vazio = { name: "", role: "", email: "", phone: "", notes: "", receivesInvoices: false };
  const [form, setForm] = useState<(typeof vazio & { id?: number }) | null>(null);
  const salvar = trpc.customers.saveContact.useMutation({
    onSuccess: () => {
      void utils.customers.listContacts.invalidate();
      setForm(null);
    },
    onError: (e) =>
      void modals.alert({
        title: t("customerDetail.contactsTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const remover = trpc.customers.removeContact.useMutation({
    onSuccess: () => void utils.customers.listContacts.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("customerDetail.contactsTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const rows = q.data?.rows ?? [];
  const campo =
    "h-9 w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface-page)] px-3 text-sm text-[var(--text-strong)] outline-none focus:border-lime";
  return (
    <section
      className="col-span-2 rounded-lg border border-neutral-800 bg-neutral-900 p-4"
      data-testid="customer-contacts"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("customerDetail.contactsTitle")}
        </h3>
        {canEdit && !form && (
          <button
            type="button"
            onClick={() => setForm({ ...vazio })}
            className="rounded-md border border-lime/60 px-3 py-1 text-xs font-semibold hover:bg-lime/15"
            data-testid="contact-add"
          >
            + {t("customerDetail.contactAddBtn")}
          </button>
        )}
      </div>
      {rows.length === 0 && !form && (
        <p className="text-sm text-neutral-500">{t("customerDetail.contactsEmpty")}</p>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-neutral-800">
          {rows.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
              data-testid="contact-row"
            >
              <span className="min-w-0">
                <span className="font-semibold" style={{ color: "var(--text-strong)" }}>
                  {c.name}
                </span>
                {c.role && <span className="text-neutral-500"> · {c.role}</span>}
                {c.receivesInvoices && (
                  <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-lime/15 text-lime">
                    ✉ {t("customerDetail.contactReceivesInvoices")}
                  </span>
                )}
                <span className="block text-xs text-neutral-400">
                  {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                  {c.notes ? ` — ${c.notes}` : ""}
                </span>
              </span>
              {canEdit && (
                <span className="flex gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        id: c.id,
                        name: c.name,
                        role: c.role ?? "",
                        email: c.email ?? "",
                        phone: c.phone ?? "",
                        notes: c.notes ?? "",
                        receivesInvoices: c.receivesInvoices,
                      })
                    }
                    className="text-lime hover:underline"
                  >
                    {t("customerDetail.contactEditBtn")}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await modals.confirm({
                        title: t("customerDetail.contactRemoveTitle"),
                        message: c.name,
                        tone: "danger",
                      });
                      if (ok) remover.mutate({ companyId, id: c.id });
                    }}
                    className="text-red-400 hover:underline"
                  >
                    {t("customerDetail.contactRemoveBtn")}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {form && (
        <div
          className="mt-3 grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-2"
          style={{ borderColor: "var(--border-strong)", background: "var(--surface-muted)" }}
          data-testid="contact-form"
        >
          <label className="text-xs text-neutral-400">
            {t("customerDetail.contactName")} *
            <input
              className={campo + " mt-1"}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name"
              autoFocus
            />
          </label>
          <label className="text-xs text-neutral-400">
            {t("customerDetail.contactRole")}
            <input
              className={campo + " mt-1"}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder={t("customerDetail.contactRolePh")}
            />
          </label>
          <label className="text-xs text-neutral-400">
            {t("customerDetail.email")}
            <input
              type="email"
              className={campo + " mt-1"}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email"
            />
          </label>
          <label className="text-xs text-neutral-400">
            {t("customerDetail.phone")}
            <input
              className={campo + " mt-1"}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </label>
          <label className="text-xs text-neutral-400 md:col-span-2">
            {t("customerDetail.contactNotes")}
            <input
              className={campo + " mt-1"}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={form.receivesInvoices}
              onChange={(e) => setForm({ ...form, receivesInvoices: e.target.checked })}
              className="accent-lime"
              data-testid="contact-receives"
            />
            {t("customerDetail.contactReceivesInvoicesLong")}
          </label>
          <div className="flex justify-end gap-2 md:col-span-2">
            <button
              type="button"
              onClick={() => setForm(null)}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs"
            >
              {t("customerDetail.contactCancelBtn")}
            </button>
            <button
              type="button"
              disabled={!form.name.trim() || salvar.isPending}
              onClick={() =>
                salvar.mutate({
                  companyId,
                  customerId,
                  id: form.id,
                  name: form.name.trim(),
                  role: form.role.trim() || undefined,
                  email: form.email.trim() || undefined,
                  phone: form.phone.trim() || undefined,
                  notes: form.notes.trim() || undefined,
                  receivesInvoices: form.receivesInvoices,
                })
              }
              className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
              data-testid="contact-save"
            >
              {salvar.isPending ? "…" : t("customerDetail.contactSaveBtn")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// TAB: DOSSIERS
// ============================================================================

function TabDossiers({
  companyId,
  customerId,
  range,
  etapa,
  onEtapa,
}: {
  companyId: number;
  customerId: number;
  range: { periodStart?: string; periodEnd?: string };
  etapa: Etapa | null;
  onEtapa: (e: Etapa | null) => void;
}) {
  const t = useT();
  const DOSSIER_STATUS_LABELS: Record<string, string> = {
    pending: t("customerDetail.statusPending"),
    issued: t("customerDetail.statusIssued"),
    paid: t("customerDetail.statusPaid"),
    cancelled: t("customerDetail.statusCancelled"),
  };
  const listQuery = trpc.customers.listDossiers.useQuery({
    companyId,
    customerId,
    ...range,
    ...(etapa ? { stage: etapa } : {}),
    limit: 200,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];
  const contagem = listQuery.data?.stageCounts;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("customerDetail.customerDossiers")}
        </h3>
        <span className="flex items-center gap-3 text-xs text-neutral-500">
          {listQuery.data?.total ?? 0} {t("customerDetail.total")}
          <Link
            href={`/dossiers?cliente=${customerId}`}
            className="rounded-md border border-neutral-800 px-2 py-0.5 text-lime hover:border-lime"
            data-testid="customer-dossiers-see-carros"
          >
            {t("customerDetail.seeInCarros")}
          </Link>
        </span>
      </div>
      {/* CEO 2026-11-03 — etapa atual (todas as datas), cores da lista de veículos. */}
      <div className="flex flex-wrap gap-2" data-testid="customer-dossier-stages">
        <button
          type="button"
          onClick={() => onEtapa(null)}
          className={`rounded-md border px-3 py-1 text-xs ${etapa == null ? "border-lime bg-lime/15 text-lime" : "border-neutral-700 hover:border-lime"}`}
          data-testid="customer-dossier-stage-all"
        >
          {t("customerDetail.stageAll")}
        </button>
        {ETAPAS.map((e) => {
          const n = contagem?.[e] ?? 0;
          if (n === 0 && etapa !== e) return null;
          const on = etapa === e;
          return (
            <button
              key={e}
              type="button"
              onClick={() => onEtapa(on ? null : e)}
              style={{
                background: ETAPA_STYLE[e].bg,
                color: "#fff",
                opacity: etapa && !on ? 0.45 : 1,
                outline: on ? "2px solid #fff" : "none",
                outlineOffset: -3,
              }}
              className="rounded-md px-3 py-1 text-xs font-bold"
              data-testid={`customer-dossier-stage-${e}`}
            >
              {t(ETAPA_STYLE[e].label as Parameters<typeof t>[0])} <span className="ml-1">{n}</span>
            </button>
          );
        })}
      </div>
      {etapa && <p className="text-xs text-neutral-500">{t("customerDetail.stageHint")}</p>}

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("customerDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("customerDetail.emptyDossiers")}
        </p>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colCode")}</th>
                <th className="text-left px-3 py-2 w-28">{t("customerDetail.colDate")}</th>
                <th className="text-left px-3 py-2 w-36">{t("customerDetail.colInvoiceNumber")}</th>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colPlate")}</th>
                <th className="text-right px-3 py-2 w-32 font-mono">
                  {t("customerDetail.colRetention")}
                </th>
                <th className="text-left px-3 py-2 w-36">{t("customerDetail.colSituation")}</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link href={`/dossiers/${d.id}`} className="text-lime hover:text-lime">
                      {d.dossierCode}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{d.invoiceDate ?? "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {d.invoiceNumberExternal ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {d.plate ?? d.vin ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-neutral-400">
                    {formatMoney(d.customerRetentionAmount, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2">
                    {d.stage ? (
                      <span
                        style={{
                          background: (
                            VEHICLE_STATUS_STYLE[d.stage] ?? VEHICLE_STATUS_STYLE.cadastro!
                          ).bg,
                          color: "#fff",
                        }}
                        className="inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-bold"
                        data-testid={`customer-dossier-situation-${d.id}`}
                      >
                        {t(
                          (VEHICLE_STATUS_STYLE[d.stage] ?? VEHICLE_STATUS_STYLE.cadastro!)
                            .label as Parameters<typeof t>[0],
                        )}
                      </span>
                    ) : (
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${
                          DOSSIER_STATUS_TONE[d.customerInvoiceStatus] ?? ""
                        }`}
                      >
                        {DOSSIER_STATUS_LABELS[d.customerInvoiceStatus] ?? d.customerInvoiceStatus}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      href={`/dossiers/${d.id}`}
                      className="rounded-md border border-neutral-800 hover:border-lime text-lime px-2 py-0.5 text-xs"
                    >
                      {t("customerDetail.openBtn")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: FATURAS
// ============================================================================

function TabFaturas({
  companyId,
  customerId,
  range,
}: {
  companyId: number;
  customerId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  const t = useT();
  const INVOICE_STATUS_LABELS: Record<string, string> = {
    issued: t("customerDetail.statusIssued"),
    paid: t("customerDetail.statusPaid"),
    cancelled: t("customerDetail.statusCancelled"),
    void: t("customerDetail.statusVoid"),
    uncollectible: t("customerDetail.statusUncollectible"),
  };
  // F-fatura-detalhe (CEO 2026-10-12) — clicar na fatura abre o painel dela.
  const [detailId, setDetailId] = useState<number | null>(null);
  const listQuery = trpc.customers.listInvoices.useQuery({
    companyId,
    customerId,
    ...range,
    limit: 200,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("customerDetail.customerInvoices")}
        </h3>
        <span className="text-xs text-neutral-500">
          {listQuery.data?.total ?? 0} {t("customerDetail.total")}
        </span>
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("customerDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("customerDetail.emptyInvoices")}
        </p>
      )}
      {detailId != null && (
        <InvoiceDetailModal
          companyId={companyId}
          invoiceId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-36">{t("customerDetail.colInvoiceNumber")}</th>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colDossier")}</th>
                <th className="text-left px-3 py-2 w-28">{t("customerDetail.colIssued")}</th>
                <th className="text-left px-3 py-2 w-28">{t("customerDetail.colDueDate")}</th>
                <th className="text-right px-3 py-2 w-32 font-mono">
                  {t("customerDetail.colTotalHt")}
                </th>
                <th className="text-left px-3 py-2 w-24">{t("customerDetail.statusLabel")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((inv) => {
                const irrecuperavel = inv.fiscalStatus === "issued" && inv.uncollectibleAt != null;
                const isOverdue =
                  inv.fiscalStatus === "issued" &&
                  !irrecuperavel &&
                  inv.dueAt != null &&
                  inv.dueAt < today;
                const statusKey = irrecuperavel ? "uncollectible" : inv.fiscalStatus;
                return (
                  <tr
                    key={inv.id}
                    className={`cursor-pointer hover:bg-neutral-800/60 ${isOverdue ? "bg-red-500/5" : ""}`}
                    onClick={() => setDetailId(inv.id)}
                    title={t("customerDetail.openInvoiceTitle")}
                    data-testid={`customer-invoice-${inv.id}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-lime">{inv.invoiceNumber}</td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-400">
                      {inv.dossierId != null ? (
                        <Link
                          href={`/dossiers/${inv.dossierId}`}
                          className="hover:text-lime"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {inv.dossierCode}
                        </Link>
                      ) : (
                        <span title={t("customerDetail.consolidatedHint")}>
                          🚗 {inv.dossierCount} {t("customerDetail.carsWordShort")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-neutral-400">
                      {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td
                      className={`px-3 py-2 text-xs ${isOverdue ? "text-red-300 font-semibold" : "text-neutral-400"}`}
                    >
                      {inv.dueAt ?? "—"}
                      {isOverdue && " ⚠"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatMoney(inv.totalFaturadoSnapshot, { decimals: 2 })}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${
                          INVOICE_STATUS_TONE[statusKey] ?? ""
                        }`}
                      >
                        {INVOICE_STATUS_LABELS[statusKey] ?? inv.fiscalStatus}
                      </span>
                      {/* CEO 2026-10-12 — data do pagamento junto do status. */}
                      {inv.lastReceivedAt && (
                        <span className="block text-[10px] text-neutral-500">
                          {t("customerDetail.receivedOnPre")}{" "}
                          {new Date(inv.lastReceivedAt).toLocaleDateString("pt-BR")}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: RECEBIMENTOS
// ============================================================================

function TabRecebimentos({
  companyId,
  customerId,
  range,
}: {
  companyId: number;
  customerId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  const t = useT();
  const listQuery = trpc.customers.listReceipts.useQuery({
    companyId,
    customerId,
    ...range,
    limit: 200,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("customerDetail.customerReceipts")}
        </h3>
        <span className="text-xs text-neutral-500">
          {listQuery.data?.total ?? 0} {t("customerDetail.total")}
        </span>
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("customerDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("customerDetail.emptyReceipts")}
        </p>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-28">{t("customerDetail.colDate")}</th>
                <th className="text-left px-3 py-2 w-36">{t("customerDetail.colInvoice")}</th>
                <th className="text-left px-3 py-2 w-44">{t("customerDetail.colVehicle")}</th>
                <th className="text-right px-3 py-2 w-32 font-mono">
                  {t("customerDetail.colAmount")}
                </th>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colMethod")}</th>
                <th className="text-left px-3 py-2">{t("customerDetail.colReference")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-xs text-neutral-400">
                    {new Date(r.receivedAt).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.invoiceNumber}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.dossierId != null ? (
                      <>
                        <Link
                          href={`/dossiers/${r.dossierId}`}
                          className="whitespace-nowrap font-mono font-semibold text-neutral-200 hover:text-lime"
                        >
                          {r.plate ?? r.vin ?? "—"}
                        </Link>
                        <span className="block text-neutral-500">
                          {[r.vehicleMake, r.vehicleModel].filter(Boolean).join(" ") || "—"}
                        </span>
                      </>
                    ) : (
                      <>
                        {/* Fatura consolidada: as placas dos carros, agrupadas. */}
                        <span className="font-mono text-neutral-200">
                          {r.consolidatedPlates ?? "—"}
                        </span>
                        <span className="block text-neutral-500">
                          {t("customerDetail.consolidatedPre")} {r.consolidatedCount}{" "}
                          {t("customerDetail.worklogCarsWord")}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatMoney(r.amount, { decimals: 2 })}
                    {r.reversesId && (
                      <span className="ml-2 rounded px-1 py-0.5 text-xs bg-amber-500/15 text-amber-300">
                        {t("customerDetail.reversalBadge")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{r.paymentMethod}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500 font-mono">
                    {r.reference ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: VEÍCULOS
// ============================================================================

/**
 * CEO 2026-11-03 ("ficaria como no eDSP?") — uma linha por veículo com a etapa
 * do ÚLTIMO dossier deste cliente (mesma consulta/cores de /veiculos), chips
 * com a contagem por etapa; clicar abre a ficha do veículo.
 */
const VEICULO_ORDEM = [
  "devis",
  "em_reparacao",
  "reparado",
  "faturado",
  "recebido_pendente",
  "recebido_pago",
  "cadastro",
] as const;
type EtapaVeiculo = (typeof VEICULO_ORDEM)[number];
const VEICULOS_POR_PAGINA = 50;

function TabVeiculos({ companyId, customerId }: { companyId: number; customerId: number }) {
  const t = useT();
  const [filtro, setFiltro] = useState<EtapaVeiculo | null>(null);
  const [offset, setOffset] = useState(0);
  const [ficha, setFicha] = useState<number | null>(null);
  const listQuery = trpc.vehicles.edspList.useQuery({
    companyId,
    customerId,
    status: filtro ?? undefined,
    limit: VEICULOS_POR_PAGINA,
    offset,
  });
  const data = listQuery.data;
  const rows = data?.rows ?? [];
  const totalGeral = data
    ? Object.values(data.counters).reduce((a, n) => a + Number(n ?? 0), 0)
    : 0;

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t("customerDetail.registeredVehicles")} ({totalGeral})
      </h3>
      <div className="flex flex-wrap gap-2" data-testid="customer-vehicle-stages">
        {VEICULO_ORDEM.map((st) => {
          const n = data?.counters?.[st] ?? 0;
          if (n === 0 && filtro !== st) return null;
          const on = filtro === st;
          const estilo = VEHICLE_STATUS_STYLE[st]!;
          return (
            <button
              key={st}
              type="button"
              onClick={() => {
                setFiltro(on ? null : st);
                setOffset(0);
              }}
              style={{
                background: estilo.bg,
                color: "#fff",
                opacity: filtro && !on ? 0.45 : 1,
                outline: on ? "2px solid #fff" : "none",
                outlineOffset: -3,
              }}
              className="rounded-md px-3 py-1 text-xs font-bold"
              data-testid={`customer-vehicle-stage-${st}`}
            >
              {t(estilo.label as Parameters<typeof t>[0])} <span className="ml-1">{n}</span>
            </button>
          );
        })}
      </div>
      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("customerDetail.loading")}</p>
      )}
      {data && rows.length === 0 && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("customerDetail.emptyVehicles")}
        </p>
      )}
      {rows.length > 0 && (
        <div className="divide-y divide-neutral-800 rounded-lg border border-neutral-800 bg-neutral-900">
          {rows.map((v) => {
            const cartoes =
              v.repairs.length > 0
                ? v.repairs
                : [{ id: 0, status: v.status, partes: 1, faturadas: 0 }];
            return (
              <button
                key={v.id}
                type="button"
                disabled={!v.latestDossierId}
                onClick={() => setFicha(v.id)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-neutral-800/40 disabled:cursor-default"
                data-testid={`customer-vehicle-row-${v.id}`}
              >
                <CarLogo make={v.make} size={36} />
                <span className="min-w-0 grow">
                  <span className="block truncate text-xs text-neutral-400">
                    {[v.make, v.model].filter(Boolean).join(" ") || "—"}
                  </span>
                  <span className="block font-mono text-sm font-bold tracking-wide">
                    {v.plate ?? v.vin ?? "—"}
                  </span>
                  {v.amount > 0 && (
                    <span className="block text-xs text-neutral-300">{formatMoney(v.amount)}</span>
                  )}
                </span>
                <span className="flex shrink-0 flex-wrap justify-end gap-1">
                  {cartoes.map((dz) => {
                    const sd = VEHICLE_STATUS_STYLE[dz.status] ?? VEHICLE_STATUS_STYLE.cadastro!;
                    return (
                      <span
                        key={dz.id}
                        style={{ background: sd.bg, color: "#fff" }}
                        className="rounded px-2 py-0.5 text-[11px] font-bold"
                      >
                        {t(sd.label as Parameters<typeof t>[0])}
                      </span>
                    );
                  })}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {(data?.total ?? 0) > VEICULOS_POR_PAGINA && (
        <Paginacao
          total={data!.total}
          pageSize={VEICULOS_POR_PAGINA}
          offset={offset}
          onOffset={setOffset}
          testId="customer-vehicles-pagination"
        />
      )}
      {ficha != null && (
        <VehicleHistoryModal
          companyId={companyId}
          vehicleId={ficha}
          onClose={() => setFicha(null)}
        />
      )}
    </section>
  );
}

// ============================================================================
// TAB: AUDIT
// ============================================================================

// ============================================================================
// TAB: WORKLOG POR ATELIÊ (F-worklog-atelier, CEO 2026-10-05)
// ============================================================================

/**
 * Worklog para o cliente com TODOS os carros feitos num ateliê dele, de todos
 * os técnicos, no período: técnico do PDR e total do devis HT por carro.
 * Baixar o PDF ou enviar ao e-mail do cliente. Só admin.
 */
type WorklogStatus = "devis" | "em_reparacao" | "repare" | "facture";

function TabWorklog({
  companyId,
  customerId,
  customerEmail,
}: {
  companyId: number;
  customerId: number;
  customerEmail: string | null;
}) {
  const t = useT();
  const hoje = new Date();
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [atelierId, setAtelierId] = useState<number | null>(null);
  // F-worklog-todos (CEO 2026-10-10) — um worklog só com todos os ateliês.
  const [todosAteliers, setTodosAteliers] = useState(false);
  const [from, setFrom] = useState(iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
  const [to, setTo] = useState(iso(hoje));
  const [email, setEmail] = useState(customerEmail ?? "");
  const [enviado, setEnviado] = useState<string | null>(null);
  // CEO 2026-10-06 (modelo eDSP): contadores Devis / Réparé / Facturé que
  // filtram a tabela. O PDF e o e-mail continuam com todos os carros.
  // CEO 2026-10-20: os chips de status são uma SELEÇÃO (o que vai no worklog,
  // na tela, no PDF e no e-mail). Padrão = tudo menos os devis.
  const [sel, setSel] = useState<Set<WorklogStatus>>(
    () => new Set<WorklogStatus>(["em_reparacao", "repare", "facture"]),
  );
  const toggleSel = (st: WorklogStatus) =>
    setSel((cur) => {
      const n = new Set(cur);
      if (n.has(st)) n.delete(st);
      else n.add(st);
      return n;
    });
  // CEO 2026-10-10 — super admin pode gerar o worklog no valor FATURADO ao
  // cliente (tela, PDF e e-mail). Nunca no portal do técnico.
  const { user: usuario } = useAuth();
  const podeFaturado = usuario?.isSuperAdmin === true;
  const [valor, setValor] = useState<"devis" | "facture">("devis");
  const valorEfetivo = podeFaturado ? valor : "devis";
  const ateliersQ = trpc.customers.listAteliers.useQuery({ companyId, customerId });
  const ateliers = (ateliersQ.data?.rows ?? []).filter((a) => a.active);
  const idEfetivo = todosAteliers
    ? null
    : (atelierId ?? (ateliers.length === 1 ? ateliers[0]!.id : null));
  const escolhido = todosAteliers || idEfetivo != null;
  const periodoOk = from <= to;
  const rowsQ = trpc.worklogs.atelierRows.useQuery(
    { companyId, customerId, atelierId: idEfetivo, from, to, valor: valorEfetivo },
    { enabled: escolhido && periodoOk },
  );
  const abaPdf = useAbaPendente();
  const pdfMut = trpc.worklogs.atelierPdf.useMutation({
    onSuccess: (r) => abaPdf.ir(r.url),
    onError: () => abaPdf.fechar(),
  });
  const emailMut = trpc.worklogs.atelierEmail.useMutation({
    onSuccess: (r) => setEnviado(r.sentTo),
  });
  const todas = rowsQ.data?.rows ?? [];
  const rows = todas.filter((r) => sel.has(r.status));
  const contagem = (st: WorklogStatus) => todas.filter((r) => r.status === st).length;
  const statusInfo: Record<WorklogStatus, { badge: string; label: string }> = {
    devis: { badge: "b-ok", label: t("customerDetail.wlStatusDevis") },
    em_reparacao: { badge: "b-mut", label: t("customerDetail.wlStatusEmReparacao") },
    repare: { badge: "b-warn", label: t("customerDetail.wlStatusRepare") },
    facture: { badge: "b-info", label: t("customerDetail.wlStatusFacture") },
  };
  const ordemStatus: WorklogStatus[] = ["devis", "em_reparacao", "repare", "facture"];
  // CEO 2026-10-07/10/20: PDF/e-mail levam exatamente o que está selecionado
  // nos chips (padrão: em reparação + reparado + faturado; devis fora).
  const feitos = rows;
  const valorLinha = (r: (typeof todas)[number]) =>
    valorEfetivo === "facture" ? (r.factureHt ?? 0) : r.devisHt;
  const totalFeitos = feitos.reduce((acc, r) => acc + valorLinha(r), 0);
  const campo = "rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm";
  const fmtData = (s: string) => s.slice(0, 10).split("-").reverse().join("/");
  const params = escolhido
    ? {
        companyId,
        customerId,
        atelierId: idEfetivo,
        from,
        to,
        valor: valorEfetivo,
        statuses: ordemStatus.filter((st) => sel.has(st)),
      }
    : null;
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {t("customerDetail.worklogTitle")}
        </h3>
        <p className="mt-1 text-xs text-neutral-500">{t("customerDetail.worklogHint")}</p>
      </div>
      {ateliersQ.isSuccess && ateliers.length === 0 ? (
        <p className="text-sm text-amber-300">{t("customerDetail.worklogNoAtelier")}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-neutral-400">
            {t("customerDetail.worklogAtelier")}
            <select
              value={todosAteliers ? "todos" : (idEfetivo ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                setTodosAteliers(v === "todos");
                setAtelierId(v && v !== "todos" ? Number(v) : null);
              }}
              className={`${campo} mt-1 block min-w-56`}
              data-testid="worklog-atelier"
            >
              <option value="">{t("customerDetail.worklogChooseAtelier")}</option>
              {ateliers.length > 1 && (
                <option value="todos">{t("customerDetail.worklogAllAteliers")}</option>
              )}
              {ateliers.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.city ? ` — ${a.city}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-neutral-400">
            {t("customerDetail.worklogFrom")}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={`${campo} mt-1 block`}
            />
          </label>
          <label className="text-xs text-neutral-400">
            {t("customerDetail.worklogTo")}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={`${campo} mt-1 block`}
            />
          </label>
          {podeFaturado && (
            <div className="text-xs text-neutral-400">
              {t("customerDetail.wlValorLabel")}
              <div className="mt-1 flex rounded-md border border-neutral-700" role="group">
                {(["devis", "facture"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setValor(v)}
                    className={`px-3 py-1.5 text-sm ${
                      valor === v
                        ? "bg-lime text-on-lime font-semibold"
                        : "bg-neutral-950 text-neutral-300"
                    }`}
                    data-testid={`worklog-valor-${v}`}
                    title={v === "facture" ? t("customerDetail.wlValorFactureHint") : undefined}
                  >
                    {v === "devis"
                      ? t("customerDetail.wlValorDevis")
                      : t("customerDetail.wlValorFacture")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {!periodoOk && <p className="text-xs text-red-400">{t("customerDetail.worklogBadPeriod")}</p>}
      {rowsQ.error && <p className="text-xs text-red-400">{rowsQ.error.message}</p>}
      {escolhido && periodoOk && rowsQ.isSuccess && (
        <>
          {todas.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="worklog-status-counts">
              {ordemStatus
                .filter((st) => st !== "em_reparacao" || contagem(st) > 0)
                .map((st) => {
                  const ativo = sel.has(st);
                  return (
                    <button
                      key={st}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => toggleSel(st)}
                      title={t("customerDetail.wlSelectHint")}
                      className={`badge ${statusInfo[st].badge} inline-flex items-center gap-2 px-3 py-1.5 text-sm ${
                        ativo ? "ring-2 ring-offset-1 ring-current" : "opacity-40 line-through"
                      }`}
                      data-testid={`worklog-count-${st}`}
                    >
                      {ativo ? "✓ " : ""}
                      {statusInfo[st].label}
                      <span className="text-base font-bold tabular-nums">{contagem(st)}</span>
                    </button>
                  );
                })}
              <button
                type="button"
                onClick={() => setSel(new Set(ordemStatus))}
                className="text-xs text-neutral-400 underline"
              >
                {t("customerDetail.wlShowAll")} ({todas.length})
              </button>
              <span className="text-[11px] text-neutral-500">
                {t("customerDetail.wlSelectHint")}
              </span>
            </div>
          )}
          <div className="overflow-x-auto rounded-md border border-neutral-800">
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t("customerDetail.worklogColDate")}</th>
                  {todosAteliers && (
                    <th className="px-3 py-2 text-left">{t("customerDetail.worklogColAtelier")}</th>
                  )}
                  <th className="px-3 py-2 text-left">{t("customerDetail.worklogColVehicle")}</th>
                  <th className="px-3 py-2 text-left">{t("customerDetail.worklogColPlate")}</th>
                  <th className="px-3 py-2 text-left">{t("customerDetail.wlColLines")}</th>
                  <th className="px-3 py-2 text-left">{t("customerDetail.wlColStatus")}</th>
                  <th className="px-3 py-2 text-right">
                    {valorEfetivo === "facture"
                      ? t("customerDetail.wlColFacture")
                      : t("customerDetail.worklogColDevis")}
                  </th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={todosAteliers ? 7 : 6} className="px-3 py-3 text-neutral-500">
                      {t("customerDetail.worklogEmpty")}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.dossierId} className="border-t border-neutral-800">
                    <td className="px-3 py-1.5 text-neutral-400">{fmtData(r.date)}</td>
                    {todosAteliers && (
                      <td className="px-3 py-1.5 text-neutral-300">
                        {r.atelierName ?? (
                          <span className="text-amber-300">{t("customerDetail.wlNoAtelier")}</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-1.5">{r.vehicle ?? "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      <Link href={`/dossiers/${r.dossierId}`} className="hover:underline">
                        {r.plate ?? r.dossierCode ?? "—"}
                      </Link>
                    </td>
                    {/* CEO 2026-10-11 — as 3 linhas de trabalho com o técnico e o valor. */}
                    <td className="px-3 py-1.5 text-neutral-300">
                      {r.lines.length === 0 ? (
                        "—"
                      ) : (
                        <div className="space-y-0.5 text-xs">
                          {r.lines.map((l, i) => {
                            const v = valorEfetivo === "facture" ? l.factureHt : l.devisHt;
                            const rotulo =
                              l.kind === "pdr"
                                ? t("customerDetail.wlLinePdr")
                                : l.kind === "depose"
                                  ? t("customerDetail.wlLineDepose")
                                  : t("customerDetail.wlLineRepose");
                            return (
                              <div key={i} className="flex flex-wrap items-baseline gap-x-1.5">
                                <span className="w-24 shrink-0 text-neutral-500">{rotulo}</span>
                                <span className={l.done ? "" : "text-amber-300"}>
                                  {l.tiers
                                    ? t("customerDetail.wlLineTiers")
                                    : (l.technician ?? "—")}
                                  {!l.done && ` · ${t("customerDetail.wlLineEnCours")}`}
                                </span>
                                <span className="ml-auto tabular-nums text-neutral-400">
                                  {v == null ? "—" : formatMoney(v, { decimals: 2 })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`badge ${statusInfo[r.status].badge}`}>
                        {statusInfo[r.status].label}
                      </span>
                      {r.invoiceNumber && (
                        <span className="ml-1.5 font-mono text-[11px] text-neutral-500">
                          {r.invoiceNumber}
                        </span>
                      )}
                      {/* CEO 2026-10-10 — devis com dégarnissage e ninguém lançou. */}
                      {r.semDesmontagem && (
                        <span
                          className="ml-1.5 text-[11px] text-amber-300"
                          title={t("customerDetail.wlNoDemontageHint")}
                        >
                          ⚠ {t("customerDetail.wlNoDemontage")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {valorEfetivo === "facture" && r.factureHt == null ? (
                        <span className="text-neutral-500">—</span>
                      ) : (
                        formatMoney(valorLinha(r), { decimals: 2 })
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-neutral-700 font-semibold">
                    <td colSpan={todosAteliers ? 6 : 5} className="px-3 py-2">
                      {rows.length} {t("customerDetail.worklogCarsWord")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatMoney(
                        rows.reduce((acc, r) => acc + valorLinha(r), 0),
                        { decimals: 2 },
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <p className="text-xs text-neutral-400" data-testid="worklog-sent-scope">
            📤 {t("customerDetail.wlSentScopePre")} {feitos.length}{" "}
            {t("customerDetail.worklogCarsWord")} · {formatMoney(totalFeitos, { decimals: 2 })}
            <span className="text-neutral-500">
              {" "}
              —{" "}
              {ordemStatus
                .filter((st) => sel.has(st))
                .map((st) => statusInfo[st].label)
                .join(", ") || "—"}
            </span>
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <button
              type="button"
              disabled={feitos.length === 0 || pdfMut.isPending || params == null}
              onClick={() => {
                if (!params) return;
                abaPdf.abrir();
                pdfMut.mutate(params);
              }}
              className="rounded-md border border-lime/50 px-3 py-1.5 text-sm text-lime disabled:opacity-40"
              data-testid="worklog-atelier-pdf"
            >
              📄 {t("customerDetail.worklogPdfBtn")}
            </button>
            <label className="text-xs text-neutral-400">
              {t("customerDetail.worklogEmailLabel")}
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEnviado(null);
                }}
                className={`${campo} mt-1 block min-w-64`}
              />
            </label>
            <button
              type="button"
              disabled={
                feitos.length === 0 || emailMut.isPending || params == null || !email.includes("@")
              }
              onClick={() => params && emailMut.mutate({ ...params, recipientEmail: email.trim() })}
              className="rounded-md bg-lime px-3 py-1.5 text-sm font-semibold text-on-lime disabled:opacity-40"
              data-testid="worklog-atelier-email"
            >
              📧 {emailMut.isPending ? "…" : t("customerDetail.worklogEmailBtn")}
            </button>
          </div>
          {pdfMut.error && <p className="text-xs text-red-400">{pdfMut.error.message}</p>}
          {emailMut.error && <p className="text-xs text-red-400">{emailMut.error.message}</p>}
          {enviado && (
            <p className="text-xs text-lime">
              ✓ {t("customerDetail.worklogSentPre")} {enviado}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function TabAudit({
  companyId,
  customerId,
  isAdmin,
}: {
  companyId: number;
  customerId: number;
  isAdmin: boolean;
}) {
  const t = useT();
  const auditQuery = trpc.audit.listByCompany.useQuery(
    {
      companyId,
      entityKind: "customer",
      entityId: customerId,
      limit: 100,
      offset: 0,
    },
    { enabled: isAdmin },
  );

  if (!isAdmin) {
    return (
      <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
        {t("customerDetail.auditRequiresAdmin")}
      </p>
    );
  }

  const rows = auditQuery.data?.rows ?? [];
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t("customerDetail.customerAuditLog")}
      </h3>
      {auditQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("customerDetail.loading")}</p>
      )}
      {rows.length === 0 && !auditQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("customerDetail.emptyAudit")}
        </p>
      )}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-neutral-950/50 text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-40">{t("customerDetail.colWhen")}</th>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colAction")}</th>
                <th className="text-left px-3 py-2 w-32">{t("customerDetail.colBy")}</th>
                <th className="text-left px-3 py-2">{t("customerDetail.colReason")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-neutral-400 font-mono">
                    {new Date(r.occurredAt).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.action}</td>
                  <td className="px-3 py-2 text-neutral-400">
                    {r.userName ?? r.userEmail ?? `#${r.userId}`}
                  </td>
                  <td className="px-3 py-2 text-amber-200">{r.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function Row({
  label,
  value,
  mono,
  badge,
}: {
  label: string;
  value: string;
  mono?: boolean;
  badge?: string;
}) {
  return (
    <div className="flex gap-2">
      <dt className="text-neutral-500 w-40 text-xs">{label}:</dt>
      <dd className={mono ? "font-mono" : ""}>
        {badge ? (
          <span className={`inline-block rounded px-2 py-0.5 text-xs ${badge}`}>{value}</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function KpiBlock({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: "amber" | "red" | "positive";
  /** F-releve-conta — segunda linha (ex.: o mesmo valor em TTC). */
  sub?: string;
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-300"
      : tone === "red"
        ? "text-red-300"
        : tone === "positive"
          ? "text-lime"
          : "";
  return (
    <div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`font-mono text-base ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";
